import * as React from 'react';

import { faFileInvoice } from '@fortawesome/pro-regular-svg-icons';
import { DialogDescription } from '@radix-ui/react-dialog';
import { FormattedMessage, useIntl } from 'react-intl';

import { DigitalReceiptCanvas } from 'src/features/statistics/components/DigitalReceiptCanvas';
import { statisticsMessages } from 'src/features/statistics/utils';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/utils/messages';

import type { ReceiptRow } from 'src/features/statistics/types/receiptRow';

interface ViewDigitalReceiptModalProps extends React.HTMLAttributes<HTMLDivElement> {
    digitalReceiptDetails?: string;
    /** Structured rows, where the endpoint returns them. Preferred over the text. */
    rows?: ReceiptRow[];
    charsPerLine?: number;
    downloadReceipt?: () => void;
}

const ViewDigitalReceiptModal = ({
    children,
    digitalReceiptDetails,
    rows,
    charsPerLine,
    downloadReceipt,
}: ViewDigitalReceiptModalProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();

    return (
        <Dialog open={isModalOpen} onOpenChange={() => setIsModalOpen(!isModalOpen)}>
            {children}
            <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={faFileInvoice} />
                        <div>
                            <DialogTitle>{formatMessage(statisticsMessages.digitalReceipt)}</DialogTitle>
                            <DialogDescription>
                                <FormattedMessage {...statisticsMessages.downloadCopyDescription} />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                {/* items-center, not justify-center: DialogBody is a flex COLUMN, so the
                    horizontal axis is the cross one. */}
                <DialogBody className="items-center">
                    <DigitalReceiptCanvas
                        rows={rows}
                        charsPerLine={charsPerLine}
                        receiptTextLines={digitalReceiptDetails?.trimEnd().split('\n')}
                    />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter className="flex-row">
                    <Button
                        variant="outline"
                        type="button"
                        className="flex-1"
                        onClick={() => setIsModalOpen(!isModalOpen)}
                    >
                        {formatMessage(generalMessages.cancel)}
                    </Button>
                    <Button onClick={downloadReceipt} className="flex-1">
                        <FormattedMessage {...statisticsMessages.downloadReceipt} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { ViewDigitalReceiptModal };
